// Full-width status strip pinned to the BOTTOM of the app that tells the user,
// in plain language, whether their realtime connection is live. Reads the
// shared `useConnectionStore` (fed by each platform's socket layer) and stays
// HIDDEN while everything is healthy — it only slides up when the connection is
// connecting/reconnecting/lost, then shows a brief "Back online" confirmation
// when it recovers. Cross-platform: React Native primitives + NativeWind, so it
// renders unchanged on web (react-native-web) and the desktop RN-macOS app.
//
// On web it's portaled to <body> (via `WebPortal`) and pinned with an inline
// `position: fixed` + max z-index so it sits above every surface regardless of
// stacking context — matching `NotificationBannerHost`. The whole strip is
// pointer-events-transparent so it never traps taps meant for the composer
// underneath. On native the portal is a passthrough and it anchors in-tree.

import { useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Animated, Platform, Text, View, type ViewStyle } from 'react-native';

import { WebPortal } from '../chat/composer/WebPortal';
import { type ConnectionState, useConnectionStore } from './connectionStore';

const IS_WEB = Platform.OS === 'web';

/** How long the "Back online" confirmation lingers before sliding away. */
const RESTORED_LINGER_MS = 2_500;
/** Grace period before an initial `connecting` shows a banner — a healthy
 *  startup connects well within this, so the bar never flickers on a good
 *  load. A drop INTO connecting/reconnecting from a live session shows at once. */
const CONNECTING_GRACE_MS = 1_200;

type Tone = 'warn' | 'error' | 'ok';

interface StatusCopy {
  readonly label: string;
  readonly tone: Tone;
}

/** Human-readable copy + tone per connection phase. */
function statusFor(state: ConnectionState): StatusCopy {
  switch (state) {
    case 'connecting':
      return { label: 'Connecting…', tone: 'warn' };
    case 'reconnecting':
      return { label: 'Reconnecting…', tone: 'warn' };
    case 'disconnected':
      return { label: 'No connection — you’re offline', tone: 'error' };
    case 'connected':
      return { label: 'Back online', tone: 'ok' };
  }
}

export function ConnectivityBanner(): JSX.Element | null {
  const state = useConnectionStore((s) => s.state);

  // What the strip is currently showing (decoupled from `state` so the
  // recovered confirmation can keep "Back online" up after state→connected).
  const [shown, setShown] = useState<ConnectionState>('connecting');
  const [visible, setVisible] = useState(false);
  // True once we've surfaced a problem this session, so a later `connected`
  // is treated as a *recovery* worth confirming (rather than a silent healthy
  // start that should show nothing).
  const troubledRef = useRef(false);

  useEffect(() => {
    if (state === 'connected') {
      if (troubledRef.current) {
        troubledRef.current = false;
        setShown('connected');
        setVisible(true);
        const t = setTimeout(() => setVisible(false), RESTORED_LINGER_MS);
        return () => clearTimeout(t);
      }
      setVisible(false);
      return;
    }

    // A bare initial `connecting` waits out the grace window so a normal fast
    // connect never flashes. Any other trouble (or connecting after we've
    // already been troubled) surfaces immediately.
    if (state === 'connecting' && !troubledRef.current) {
      const t = setTimeout(() => {
        troubledRef.current = true;
        setShown('connecting');
        setVisible(true);
      }, CONNECTING_GRACE_MS);
      return () => clearTimeout(t);
    }

    troubledRef.current = true;
    setShown(state);
    setVisible(true);
    return;
  }, [state]);

  // Slide up/down. Keep mounted through the exit animation, then unmount.
  const translateY = useRef(new Animated.Value(64)).current;
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (visible) {
      setMounted(true);
      Animated.spring(translateY, {
        toValue: 0,
        tension: 120,
        friction: 18,
        useNativeDriver: !IS_WEB,
      }).start();
    } else if (mounted) {
      Animated.timing(translateY, {
        toValue: 64,
        duration: 180,
        useNativeDriver: !IS_WEB,
      }).start(({ finished }) => {
        if (finished) setMounted(false);
      });
    }
    // `mounted` intentionally omitted — we only react to visibility flips.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  if (!mounted) return null;

  const { label, tone } = statusFor(shown);

  // Pinned full-width along the bottom edge. Inline-styled for the same reason
  // as `NotificationBannerHost`: through a portal, NativeWind's `fixed`/`z-`
  // are unreliable, so position + z-index go on `style`. `pointer-events: none`
  // keeps the strip purely informational so the composer underneath stays
  // tappable. ('fixed' is valid on react-native-web but absent from RN's
  // ViewStyle type, hence the cast.)
  const overlayStyle = (
    IS_WEB
      ? { position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 2147483600 }
      : { position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 10000 }
  ) as unknown as ViewStyle;

  return (
    <WebPortal>
      <View pointerEvents="none" style={overlayStyle}>
        {/* Animated.View carries the slide ONLY — NativeWind className does not
            apply to Animated.View, so all visuals live on the inner <View>. */}
        <Animated.View style={{ transform: [{ translateY }] }}>
          <View
            accessibilityRole="alert"
            accessibilityLiveRegion="polite"
            className="w-full flex-row items-center justify-center gap-2 border-t border-light-border-subtle dark:border-dark-border-subtle bg-light-surface-panel dark:bg-dark-surface-panel px-4 py-2"
          >
            {tone === 'warn' ? (
              <ActivityIndicator size="small" color="#F59E0B" />
            ) : (
              <View
                className={`h-2 w-2 rounded-full ${tone === 'ok' ? 'bg-[#22C55E]' : 'bg-[#EF4444]'}`}
              />
            )}
            <Text className="text-[13px] font-medium text-light-text-primary dark:text-dark-text-primary">
              {label}
            </Text>
          </View>
        </Animated.View>
      </View>
    </WebPortal>
  );
}
